import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add tenant ID header - get from storage or extract from host
  let tenantId = localStorage.getItem('tenant_id');
  if (!tenantId) {
    // Extract tenant ID from host (e.g., task-orchestrator-6.preview.emergentagent.com)
    const host = window.location.host;
    if (host.includes('.')) {
      tenantId = host.split('.')[0];
    }
  }
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  
  return config;
});

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE}/auth/refresh`, null, {
            params: { refresh_token: refreshToken },
          });
          const { access_token, refresh_token: newRefresh } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefresh);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  refresh: (token) => api.post('/auth/refresh', null, { params: { refresh_token: token } }),
  updateLanguage: (language) => api.put('/auth/language', { language }),
  getPreferences: () => api.get('/auth/preferences'),
  updatePreferences: (data) => api.put('/auth/preferences', data),
  resetPromptsToGlobal: () => api.delete('/auth/preferences/prompts'),
};

// OpenRouter APIs
export const openRouterApi = {
  setup: (apiKey) => api.post('/openrouter/setup', { api_key: apiKey }),
  status: () => api.get('/openrouter/status'),
  models: () => api.get('/openrouter/models'),
};

// Project APIs
export const projectApi = {
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  preAnalyze: (data) => api.post('/projects/pre-analyze', data),
  submitClarifications: (id, answers) => api.post(`/projects/${id}/clarifications`, answers),
  skipClarifications: (id) => api.post(`/projects/${id}/skip-clarifications`),
  configureTeam: (id, config) => api.post(`/projects/${id}/configure-team`, config),
  execute: (id, mode) => api.post(`/projects/${id}/execute`, { mode }),
  executeExpert: (id, expertId) => api.post(`/projects/${id}/execute-expert/${expertId}`),
  regenerateExpert: (id, expertId) => api.post(`/projects/${id}/experts/${expertId}/regenerate`),
  continueExecution: (id) => api.post(`/projects/${id}/continue`),
  getExperts: (id) => api.get(`/projects/${id}/experts`),
  addComment: (id, data) => api.post(`/projects/${id}/comment`, data),
  getExpertClarifications: (id, expertId) => api.get(`/projects/${id}/experts/${expertId}/clarifications`),
  sendExpertClarification: (id, expertId, data) => api.post(`/projects/${id}/experts/${expertId}/clarifications`, data),
  getVersions: (id) => api.get(`/projects/${id}/versions`),
  restoreVersion: (id, version) => api.post(`/projects/${id}/restore-version`, { version }),
  estimateCost: (id) => api.post(`/projects/${id}/estimate-cost`),
  export: (id, format) => api.get(`/projects/${id}/export`, { params: { format } }),
  share: (id) => api.post(`/projects/${id}/share`),
  getExpertSettings: (id) => api.get(`/projects/${id}/expert-settings`),
  updateExpertSettings: (id, role, settings) => api.put(`/projects/${id}/expert-settings/${encodeURIComponent(role)}`, settings),
  updateGlobalMcpServers: (id, servers) => api.put(`/projects/${id}/global-mcp-servers`, servers),
  chat: (id, message) => api.post(`/projects/${id}/chat`, { message }),
  getChatHistory: (id) => api.get(`/projects/${id}/chat-history`),
  regenerateTeam: (id, teamSize, accuracyLevel, analysisMode) => api.post(`/regenerate-team/${id}`, null, { 
    params: { team_size: teamSize, accuracy_level: accuracyLevel, analysis_mode: analysisMode } 
  }),
  recalculateModels: (id, accuracyLevel) => api.post(`/recalculate-team-models/${id}`, null, {
    params: { accuracy_level: accuracyLevel }
  }),
};

// MCP API
export const mcpApi = {
  validate: (config) => api.post('/mcp/validate', config),
};

// Shared project API
export const sharedApi = {
  get: (token) => api.get(`/shared/${token}`),
};

// Templates API
export const templatesApi = {
  list: () => api.get('/templates'),
  save: (name, description, projectId) => api.post('/templates', { name, description, project_id: projectId }),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Stats API
export const statsApi = {
  get: () => api.get('/stats'),
};

// Custom Experts API
export const customExpertsApi = {
  list: () => api.get('/custom-experts'),
  get: (id) => api.get(`/custom-experts/${id}`),
  create: (data) => api.post('/custom-experts', data),
  update: (id, data) => api.put(`/custom-experts/${id}`, data),
  delete: (id) => api.delete(`/custom-experts/${id}`),
};

// API Keys API
export const apiKeysApi = {
  list: () => api.get('/api-keys'),
  create: () => api.post('/api-keys'),
  delete: (id) => api.delete(`/api-keys/${id}`),
};

// Admin API (super_admin only)
export const adminApi = {
  getTenants: () => api.get('/admin/tenants'),
  createTenant: (data) => api.post('/admin/tenants', data),
  updateTenant: (id, data) => api.put(`/admin/tenants/${id}`, data),
  deleteTenant: (id) => api.delete(`/admin/tenants/${id}`),
  getTenantUsers: (id) => api.get(`/admin/tenants/${id}/users`),
  getStats: () => api.get('/admin/stats'),
  // Pending registrations
  getNotifications: () => api.get('/admin/notifications'),
  getPendingRegistrations: () => api.get('/admin/pending-registrations'),
  approveRegistration: (registrationId, approve, rejectionReason = null) => 
    api.post('/admin/approve-registration', { registration_id: registrationId, approve, rejection_reason: rejectionReason }),
  markNotificationRead: (notificationId) => api.post(`/admin/mark-notification-read/${notificationId}`),
  // Email settings
  getEmailSettings: () => api.get('/admin/email-settings'),
  updateEmailSettings: (data) => api.put('/admin/email-settings', data),
  testEmail: () => api.post('/admin/test-email'),
  // Global prompts (SuperAdmin only)
  getGlobalPrompts: () => api.get('/admin/global-prompts'),
  updateGlobalPrompts: (data) => api.put('/admin/global-prompts', data),
};

// Expert Feedback API
export const expertFeedbackApi = {
  record: (data) => api.post('/expert-feedback', data).catch(err => {
    // Suppress 401/422 errors for feedback recording as it's non-critical
    if (err.response && (err.response.status === 401 || err.response.status === 422)) {
      console.debug('Feedback recording skipped:', err.message);
      return { data: {} };
    }
    throw err;
  }),
  suggestModel: (expertRole) => api.get(`/expert-feedback/suggest-model/${encodeURIComponent(expertRole)}`).catch(err => {
    // Suppress errors for suggestions
    console.debug('Model suggestion skipped:', err.message);
    return { data: {} };
  }),
};

// Templates API
export const templateApi = {
  list: () => api.get('/templates'),
  create: (data) => api.post('/templates', data),
  createFromProject: (projectId, name) => api.post(`/templates/from-project/${projectId}`, { name }),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Health API
export const healthApi = {
  check: () => api.get('/health'),
};

export default api;
